/**
 * GenerationScreen — scope-parameterised generation analytics for State, DISCOM, and Vendor roles.
 * Props:
 *   scope: 'state' | 'discom' | 'vendor'
 */
import React, { useState } from 'react';
import KPICard from '../../components/shared/KPICard';
import SectionDivider from '../../components/shared/SectionDivider';
import BarChart from '../../components/charts/BarChart';
import HorizontalBarChart from '../../components/charts/HorizontalBarChart';
import {
  gujaratDailyGeneration,
  discomGenerationBars,
  pgvclGeneration,
  zebronGeneration,
} from '../../data/mockData';

type Scope = 'state' | 'discom' | 'vendor';
type Tab   = 'daily' | 'circle' | 'state';

interface GenerationScreenProps {
  scope: Scope;
}

/* ── Plants table (top or bottom performers) ─────────────────────────────── */
interface PlantRow {
  id: string;
  consumer: string;
  capacityKWp: number;
  todayMWh?: number;
  todayKWh?: number;
  prPercent: number;
}

function PlantsTable({ plants, isTop }: { plants: PlantRow[]; isTop: boolean }) {
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="border-b border-border">
          <th className="text-left py-2 text-text-tertiary font-medium">Plant ID</th>
          <th className="text-left py-2 text-text-tertiary font-medium">Consumer</th>
          <th className="text-right py-2 text-text-tertiary font-medium">kWp</th>
          <th className="text-right py-2 text-text-tertiary font-medium">Today</th>
          <th className="text-right py-2 text-text-tertiary font-medium">PR%</th>
        </tr>
      </thead>
      <tbody>
        {plants.map(p => (
          <tr key={p.id} className={`border-b border-border/50 hover:bg-bg-secondary cursor-pointer ${!isTop ? 'bg-orange-50/30' : ''}`}>
            <td className="py-2.5 font-mono text-text-primary">{p.id}</td>
            <td className="py-2.5 text-text-secondary">{p.consumer}</td>
            <td className="py-2.5 text-right text-text-secondary">{p.capacityKWp}</td>
            <td className="py-2.5 text-right font-medium text-text-primary">
              {p.todayMWh !== undefined ? `${p.todayMWh.toFixed(2)} MWh` : `${p.todayKWh} kWh`}
            </td>
            <td className="py-2.5 text-right">
              <span className={`font-semibold ${p.prPercent >= 75 ? 'text-success' : 'text-warning'}`}>
                {p.prPercent.toFixed(1)}%
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ── State scope ─────────────────────────────────────────────────────────── */
function StateGeneration() {
  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <KPICard title="Today's GWh" value="9.84 GWh" subtext="+3.2% vs yesterday" subtextColor="green" />
        <KPICard title="Monthly GWh" value="241 GWh" subtext="Apr 2026" />
        <KPICard title="Lifetime TWh" value="0.84 TWh" subtext="Since platform launch" />
        <KPICard title="Peak MW Today" value="1,124 MW" subtext="At 12:00 PM" />
        <KPICard title="Avg PR" value="78.2%" subtext="Gujarat — Good zone" subtextColor="green" />
      </div>

      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <SectionDivider title="Date-wise Generation" subtitle="Gujarat — Last 15 days (GWh)" />
        <BarChart data={gujaratDailyGeneration} unit=" GWh" height={260} />
      </div>

      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <SectionDivider title="DISCOM-wise Generation Today" subtitle="GWh" />
        <HorizontalBarChart data={discomGenerationBars} unit=" GWh" defaultColor="#2472C8" />
      </div>
    </div>
  );
}

/* ── DISCOM scope ────────────────────────────────────────────────────────── */
function DiscomGeneration() {
  const [tab, setTab] = useState<Tab>('daily');

  const tabCls = (t: Tab) =>
    `px-3 py-1.5 text-xs rounded-input border transition-colors ${
      tab === t
        ? 'bg-navy text-white border-navy'
        : 'bg-white text-text-secondary border-border hover:border-navy hover:text-navy'
    }`;

  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <KPICard title="Today's Generation"    value="1,824 MWh"  subtext="+2.8% vs yesterday" subtextColor="green" />
        <KPICard title="This Month"            value="44,820 MWh" subtext="Apr 2026" />
        <KPICard title="Lifetime Total"        value="182 GWh"    subtext="PGVCL cumulative" />
        <KPICard title="Peak Power Today"      value="96.2 MW"    subtext="At 12:00 PM" />
        <KPICard title="Avg Specific Yield"    value={`${pgvclGeneration.avgSpecificYield} kWh/kWp`} subtext="PGVCL average" />
        <KPICard title="Avg Performance Ratio" value="78.9%"      subtext="PGVCL average" subtextColor="green" />
      </div>

      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <div className="flex items-center justify-between mb-4">
          <SectionDivider title="Date-wise Generation" subtitle="PGVCL — Last 15 days (MWh)" />
          <div className="flex items-center gap-1.5">
            <button className={tabCls('daily')}  onClick={() => setTab('daily')}>Daily</button>
            <button className={tabCls('circle')} onClick={() => setTab('circle')}>By Circle</button>
          </div>
        </div>
        {tab === 'daily' && (
          <BarChart data={pgvclGeneration.daily} unit=" MWh" height={260} />
        )}
        {tab === 'circle' && (
          <HorizontalBarChart data={pgvclGeneration.circleWise} unit=" MWh" defaultColor="#2472C8" />
        )}
      </div>

      <div className="flex gap-4">
        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="Top 5 Plants Today" subtitle="Highest generation (MWh)" />
          <PlantsTable plants={pgvclGeneration.topPlants} isTop={true} />
        </div>
        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="Bottom 5 Plants Today" subtitle="Lowest PR% — needs attention" />
          <PlantsTable plants={pgvclGeneration.bottomPlants} isTop={false} />
        </div>
      </div>
    </div>
  );
}

/* ── Vendor scope ────────────────────────────────────────────────────────── */
function VendorGeneration() {
  const [tab, setTab] = useState<Tab>('daily');

  const tabCls = (t: Tab) =>
    `px-3 py-1.5 text-xs rounded-input border transition-colors ${
      tab === t
        ? 'bg-navy text-white border-navy'
        : 'bg-white text-text-secondary border-border hover:border-navy hover:text-navy'
    }`;

  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <KPICard title="Today's Generation"    value="3,096 MWh"  subtext="+2.1% vs yesterday" subtextColor="green" />
        <KPICard title="This Month"            value="76,420 MWh" subtext="Apr 2026" />
        <KPICard title="Lifetime Total"        value="312 GWh"    subtext="Zebron portfolio" />
        <KPICard title="Peak Power Today"      value={`${zebronGeneration.peakPowerMW} MW`} subtext="Portfolio peak" />
        <KPICard title="Avg Specific Yield"    value={`${zebronGeneration.avgSpecificYield} kWh/kWp`} subtext="Portfolio average" />
        <KPICard title="Avg Performance Ratio" value="78.4%"      subtext="Portfolio average" subtextColor="green" />
      </div>

      <div className="bg-white rounded-card border border-border shadow-card p-4">
        <div className="flex items-center justify-between mb-4">
          <SectionDivider title="Date-wise Generation" subtitle="Zebron portfolio — Last 15 days (MWh)" />
          <div className="flex items-center gap-1.5">
            <button className={tabCls('daily')} onClick={() => setTab('daily')}>Daily</button>
            <button className={tabCls('state')} onClick={() => setTab('state')}>By State</button>
          </div>
        </div>
        {tab === 'daily' && (
          <BarChart data={zebronGeneration.daily} unit=" MWh" height={260} />
        )}
        {tab === 'state' && (
          <HorizontalBarChart data={zebronGeneration.stateWise} unit=" MWh" defaultColor="#2472C8" />
        )}
      </div>

      <div className="flex gap-4">
        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="Top 5 Plants Today" subtitle="Highest generation" />
          <PlantsTable plants={zebronGeneration.topPlants.map(p => ({ ...p, todayKWh: p.todayKWh }))} isTop={true} />
        </div>
        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="Bottom 5 Plants Today" subtitle="Lowest PR% — needs attention" />
          <PlantsTable plants={zebronGeneration.bottomPlants.map(p => ({ ...p, todayKWh: p.todayKWh }))} isTop={false} />
        </div>
      </div>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
export default function GenerationScreen({ scope }: GenerationScreenProps) {
  return (
    <div className="p-6">
      {scope === 'state'  && <StateGeneration />}
      {scope === 'discom' && <DiscomGeneration />}
      {scope === 'vendor' && <VendorGeneration />}
    </div>
  );
}
